// O CLIENTE DA BARREIRA DE TEXTO — `POST /moderar-texto`.
//
// A rota existe no backend desde 09/09 e nunca teve quem a chamasse. A razão registrada no
// `BL-2.5` era honesta — o ato de publicar não existia —, e ela deixou de valer em 12/09.
//
// ── O LOTE, E POR QUE ELE É UMA CHAMADA SÓ ──────────────────────────────────────────────────
//
// Um quadrinho de dez páginas tem dezenas de balões. Conferir de um em um seriam dezenas de
// requisições para publicar uma vez — e a rota já foi desenhada para receber lote justamente
// por isso (o comentário dela diz, na letra: "um quadrinho tem muitos balões").
//
// ── O QUE ACONTECE QUANDO A BARREIRA NÃO RESPONDE ───────────────────────────────────────────
//
// Esta é a decisão que importa nesta peça, e ela é o OPOSTO da tradução de cena.
//
// Na tradução, toda falha devolve o português e a criança recebe o desenho: o pior caso é a
// plataforma se comportar como ontem. Aqui, o pior caso de deixar passar não é uma imagem menos
// bonita — é ofensa publicada na biblioteca da escola com o nome de uma criança embaixo.
//
// Então **falha de rede aqui RECUSA a publicação**, com uma mensagem que diz que foi a conferência
// que não deu, e não o texto. O rascunho não se perde: ele continua em "Meus quadrinhos", e
// publicar de novo custa um clique. Falhar fechado é a escolha certa quando o dano de um lado é
// reversível em um clique e o do outro é irreversível.

import { auth } from "./firebase.class";

export interface TextoRecusado {
  indice: number;
  motivo: string;
  mensagem: string;
}

export interface VeredictoDoLote {
  permitido: boolean;
  recusados: TextoRecusado[];
}

/** Quanto se espera pela conferência. Ela é local ao servidor — não chama LLM nem rede externa. */
const PRAZO_MS = 10000;

/** Erro de CONFERÊNCIA, e não de conteúdo. A tela distingue os dois na mensagem. */
export class ConferenciaIndisponivelError extends Error {}

export async function conferirTextos(
  textos: string[],
  opcoes: { buscar?: typeof fetch; prazoMs?: number } = {}
): Promise<VeredictoDoLote> {
  if (textos.length === 0) return { permitido: true, recusados: [] };

  const buscar = opcoes.buscar ?? fetch;
  const controle = new AbortController();
  const alarme = setTimeout(() => controle.abort(), opcoes.prazoMs ?? PRAZO_MS);

  try {
    const idToken = auth?.currentUser ? await auth.currentUser.getIdToken() : null;

    const resposta = await buscar(`${process.env.REACT_APP_BACKEND_ROUTE}/moderar-texto`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      signal: controle.signal,
      body: JSON.stringify({ textos }),
    });

    if (!resposta.ok) {
      throw new ConferenciaIndisponivelError(`a conferência respondeu HTTP ${resposta.status}`);
    }

    const corpo = (await resposta.json()) as Partial<VeredictoDoLote>;
    // Resposta sem o formato esperado é indistinguível de barreira desligada — e "não sei" aqui
    // não pode virar "pode".
    if (typeof corpo?.permitido !== "boolean" || !Array.isArray(corpo.recusados)) {
      throw new ConferenciaIndisponivelError("a conferência respondeu num formato que não conheço");
    }
    return { permitido: corpo.permitido, recusados: corpo.recusados };
  } catch (e) {
    if (e instanceof ConferenciaIndisponivelError) throw e;
    throw new ConferenciaIndisponivelError(
      e instanceof Error && e.name === "AbortError"
        ? "a conferência demorou demais"
        : "não consegui falar com a conferência"
    );
  } finally {
    clearTimeout(alarme);
  }
}
